import { settingsPaths } from '@omnea-app/fe-authz/links/settingsPaths'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import {
  useGetDynamicAssigneesWithArchivedScopeCount,
  useGetMissingDynamicAssigneesCount,
} from '@omnea-app/fe-budget-approvals/useBudgetApprovals'
import { useListWorkflows } from '@omnea-app/fe-reporting/useListWorkflows'
import { WorkflowStateEnum } from '@omnea-app/packages/universal/workflow/workflowEnums'

export type OrgConfigIssueKey = 'broken-workflows' | 'missing-assignees' | 'archived-core-data'

export type OrgConfigIssueSeverity = 'error' | 'warning'

export type OrgConfigIssue = {
  key: OrgConfigIssueKey
  severity: OrgConfigIssueSeverity
  count: number
  href: string
}

export const useOrgConfigIssuesBannerInfo = () => {
  const routeAccess = useRouteAccess()
  const workflowsAnswer = routeAccess.get('settingsWorkflows')
  const dynamicAssigneesAnswer = routeAccess.get('settingsDynamicAssignees')

  const canFixWorkflows = workflowsAnswer === 'allowed'
  const canFixDynamicAssignees = dynamicAssigneesAnswer === 'allowed'
  const isAccessLoading = workflowsAnswer === 'pending' || dynamicAssigneesAnswer === 'pending'

  const { data: workflowsData, isLoading: isWorkflowsLoading } = useListWorkflows({
    skip: !canFixWorkflows,
    state: [WorkflowStateEnum.UNDER_MAINTENANCE],
  })
  const { data: missingAssigneesData, isLoading: isMissingAssigneesLoading } = useGetMissingDynamicAssigneesCount({
    skip: !canFixDynamicAssignees,
  })
  const { data: archivedScopeData, isLoading: isArchivedScopeLoading } = useGetDynamicAssigneesWithArchivedScopeCount({
    skip: !canFixDynamicAssignees,
  })

  const isLoading = isAccessLoading || isWorkflowsLoading || isMissingAssigneesLoading || isArchivedScopeLoading

  const issues: OrgConfigIssue[] = [
    {
      key: 'broken-workflows',
      severity: 'error',
      count: workflowsData?.workflows.length ?? 0,
      href: settingsPaths.workflows.list(),
    },
    {
      key: 'missing-assignees',
      severity: 'warning',
      count: missingAssigneesData?.count ?? 0,
      href: '/settings/dynamic-assignees',
    },
    {
      key: 'archived-core-data',
      severity: 'warning',
      count: archivedScopeData?.count ?? 0,
      href: '/settings/dynamic-assignees',
    },
  ]

  const activeIssues = issues.filter((issue) => issue.count > 0)

  const severity: OrgConfigIssueSeverity = activeIssues.some((issue) => issue.severity === 'error') ? 'error' : 'warning'

  return {
    shouldShowBanner: activeIssues.length > 0,
    issues: activeIssues,
    severity,
    headlineCount: activeIssues.filter((issue) => issue.severity === severity).reduce((total, issue) => total + issue.count, 0),
    isLoading,
  }
}
